import get from "lodash/get";
import { addTokensToLocalStorage, getUserDetails, putUserDetails } from "../../helper/utils";
import { setIsLogin, setUserDetails } from "../../store/actions";

const PASSWORDLESS_HEADERS = {
  rid: "thirdpartypasswordless",
  "Content-Type": "application/json"
};

const isEmail = (value = "") => value.includes("@");

const toDigits = (value = "") => value.replace(/\D/g, "");
const toIndianLocalDigits = (value = "") => {
  const digits = toDigits(value);

  if (digits.length === 12 && digits.startsWith("91")) {
    return digits.slice(2);
  }

  if (digits.length === 11 && digits.startsWith("0")) {
    return digits.slice(1);
  }

  if (digits.length > 10) {
    return digits.slice(-10);
  }

  return digits;
};

const toIndianE164 = (value = "") => {
  const localDigits = toIndianLocalDigits(value);
  return `+91${localDigits}`;
};

const splitName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);

  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" "),
    displayName: parts.join(" ")
  };
};

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const requestJson = async (url, options) => {
  const response = await fetch(url, {
    credentials: "include",
    ...options
  });
  const data = await parseJsonSafely(response);

  if (!response.ok) {
    const message = data?.message || data?.errorMessage || "Something went wrong. Please try again.";
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return { response, data };
};

const buildIdentifierPayload = (identifier = "") => {
  const value = identifier.trim();

  return isEmail(value) ? { email: value.toLowerCase() } : { phoneNumber: toIndianE164(value) };
};

export const normalizeIdentifier = (identifier = "") => {
  const value = identifier.trim();
  return isEmail(value) ? value.toLowerCase() : toIndianE164(value);
};

export const getIdentifierKind = (identifier = "") => (isEmail(identifier.trim()) ? "email" : "phone");
export const isValidIndianMobile = (value = "") => /^[6-9]\d{9}$/.test(toIndianLocalDigits(value));

export const detectExistingSubscriber = async (ssoEndpoint, identifier) => {
  const normalized = normalizeIdentifier(identifier);
  const kind = getIdentifierKind(normalized);
  const url =
    kind === "email"
      ? `${ssoEndpoint}/auth/signup/email/exists?email=${encodeURIComponent(normalized)}`
      : `${ssoEndpoint}/auth/signup/phonenumber/exists?phoneNumber=${encodeURIComponent(normalized)}`;

  try {
    const { data } = await requestJson(url, {
      method: "GET",
      headers: { rid: "thirdpartypasswordless" }
    });

    if (data?.status !== "OK") {
      return null;
    }

    if (typeof data?.doesExist === "boolean") {
      return data.doesExist;
    }

    if (typeof data?.exists === "boolean") {
      return data.exists;
    }

    if (typeof data?.userExists === "boolean") {
      return data.userExists;
    }

    if (typeof data?.isNewUser === "boolean") {
      return !data.isNewUser;
    }

    return null;
  } catch (error) {
    if (error?.status === 404) {
      return null;
    }

    throw error;
  }
};

export const sendOtpCode = async (ssoEndpoint, identifier) => {
  const { data } = await requestJson(`${ssoEndpoint}/auth/signinup/code`, {
    method: "POST",
    headers: PASSWORDLESS_HEADERS,
    body: JSON.stringify(buildIdentifierPayload(identifier))
  });

  if (data?.status && data.status !== "OK") {
    throw new Error(data?.message || "We could not send the OTP. Please try again.");
  }

  return data;
};

export const verifyOtpCode = async (ssoEndpoint, payload) => {
  const { response, data } = await requestJson(`${ssoEndpoint}/auth/signinup/code/consume`, {
    method: "POST",
    headers: PASSWORDLESS_HEADERS,
    body: JSON.stringify(payload)
  });

  if (data?.status === "GENERAL_ERROR") {
    throw new Error(data?.message || "We could not verify the OTP. Please try again.");
  }

  return {
    ...data,
    accessToken: response.headers.get("st-access-token"),
    refreshToken: response.headers.get("st-refresh-token")
  };
};

export const createSubscriptionSession = async ({ accessToken, refreshToken }) => {
  if (!accessToken || !refreshToken) {
    throw new Error("We could not create your session. Please try again.");
  }

  addTokensToLocalStorage(accessToken, refreshToken);
  const user = await getUserDetails();

  if (!user || user?.status === 401) {
    throw new Error("We could not fetch your account details. Please try again.");
  }

  window.localStorage.setItem("user", JSON.stringify(user));
  return user;
};

export const updateSubscriberProfile = async (fullName) => {
  const profileUpdate = splitName(fullName);

  if (!profileUpdate.first_name) {
    throw new Error("Please enter your name.");
  }

  await putUserDetails(profileUpdate);

  const currentUser = JSON.parse(window.localStorage.getItem("user") || "{}");
  const nextUser = {
    ...currentUser,
    metadata: {
      ...currentUser?.metadata,
      ...profileUpdate
    }
  };

  window.localStorage.setItem("user", JSON.stringify(nextUser));
  return nextUser;
};

export const requestAccessTypeDhToken = async (ssoEndpoint) => {
  const accessToken = window.localStorage.getItem("access_token");
  const headers = {
    "Content-Type": "application/json"
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const { response, data } = await requestJson(`${ssoEndpoint}/auth/accesstype-dh-token`, {
    method: "POST",
    headers
  });

  const token =
    data?.token ||
    data?.jwt ||
    data?.accesstypeJwt ||
    data?.accessTypeToken ||
    response.headers.get("x-integration-token") ||
    response.headers.get("authorization");

  if (!token) {
    throw new Error("AccessType JWT missing from accesstype-dh-token response.");
  }

  return String(token).replace(/^Bearer\s+/i, "");
};

export const handleLogout = (qt) => {
  const sso = get(qt, ["config", "publisher-attributes", "sso_endpoint"]);
  if (sso) return (window.location.href = `${sso}/auth/logout?redirectUrl=${encodeURIComponent(window.location.href)}`);
  ["access_token", "refresh_token", "user"].forEach((k) => localStorage.removeItem(k));
  window.location.reload();
};

export const handleSessionDetection = (dispatch, isLogin) => {
  if (typeof window === "undefined") return;
  const s = window.location.search;
  if (!s.includes("access_token") || isLogin) return;
  const raw = s.replace(/\?/g, "&").replace("&", "?"), p = new URLSearchParams(raw), at = p.get("access_token"), rt = p.get("refresh_token");
  if (at && rt) {
    createSubscriptionSession({ accessToken: at, refreshToken: rt }).then((u) => {
      dispatch(setIsLogin(true));
      dispatch(setUserDetails(u));
      p.delete("access_token");
      p.delete("refresh_token");
      const next = window.location.pathname + (p.toString() ? `?${p.toString()}` : "") + window.location.hash;
      window.history.replaceState(null, "", next);
    }).catch(() => {});
  }
};
